"use client";

// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → now central widget_impression
// (TrackedWidget in NewPage); see webapp/docs/analytics
// import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import Image from "next/image";
import { useMemo, useRef, type FC, type ReactNode } from "react";

/** One flat metadata row = a single key-skill / info card. */
export interface ProgramStructure5CardRow {
  id?: string;
  card_image?: string;
  card_image_alt?: string;
  card_title?: string;
  card_description?: string;
  priority?: number;
  status?: boolean;
}

export interface ProgramStructure5Payload {
  status?: boolean;
  section_background_image?: string;
  section_background_image_alt?: string;
  section_overlay_rgba?: string;
  section_background_color?: string;
  headline_primary?: string;
  headline_primary_color?: string;
  headline_accent_color?: string;
  section_description?: string;
  metadata?: ProgramStructure5CardRow[];
}

interface ProgramStructure5Props {
  data: ProgramStructure5Payload | { data: ProgramStructure5Payload };
  className?: string;
}

interface CardItem {
  id: string;
  image: string;
  imageAlt: string;
  title: string;
  description: string;
}

function resolvePayload(
  raw: ProgramStructure5Props["data"],
): ProgramStructure5Payload {
  const anyData = raw as { data?: ProgramStructure5Payload };
  if (anyData?.data && typeof anyData.data === "object") {
    return anyData.data;
  }
  return (raw as ProgramStructure5Payload) || {};
}

/**
 * Render a headline that uses `{ }` markers to highlight words in the accent
 * color: `Skills You'll {Master}` colors "Master" with `highlightColor`.
 */
function renderHeadline(text: string, highlightColor: string): ReactNode {
  if (!text) return null;
  const re = /\{([^}]+)\}/g;
  const nodes: ReactNode[] = [];
  let last = 0;
  let key = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    nodes.push(
      <span key={`hl-${key++}`} style={{ color: highlightColor }}>
        {m[1]}
      </span>,
    );
    last = re.lastIndex;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

/** Sort by priority, drop inactive/empty rows, normalize into render-ready cards. */
function buildCards(rows: ProgramStructure5CardRow[]): CardItem[] {
  return rows
    .filter(
      (r) =>
        r &&
        r.status !== false &&
        String(r.card_title ?? "").trim().length > 0,
    )
    .slice()
    .sort((a, b) => (Number(a.priority) || 0) - (Number(b.priority) || 0))
    .map((r, i) => ({
      id: String(r.id ?? i),
      image: String(r.card_image ?? "").trim(),
      imageAlt: String(r.card_image_alt ?? "").trim(),
      title: String(r.card_title ?? "").trim(),
      description: String(r.card_description ?? "").trim(),
    }));
}

const ProgramStructure5: FC<ProgramStructure5Props> = ({ data, className }) => {
  const payload = resolvePayload(data);
  const ref = useRef<HTMLElement>(null);

  const {
    section_background_image: bgImage = "",
    section_background_image_alt: bgAlt = "",
    section_overlay_rgba: overlayRgba = "",
    section_background_color = "#0B0F14",
    headline_primary = "",
    headline_primary_color = "#FFFFFF",
    headline_accent_color = "#F5C542",
    section_description = "",
  } = payload;

  const cards = useMemo(
    () => buildCards(Array.isArray(payload.metadata) ? payload.metadata : []),
    [payload.metadata],
  );

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "program_structure_section");
  //   }
  // }, []);

  if (payload.status === false) return null;
  if (cards.length === 0) return null;

  const bgSrc = (bgImage || "").trim();
  const overlayTrim = (overlayRgba || "").trim();

  return (
    <section
      ref={ref}
      className={cn(
        "relative flex w-full flex-col items-center overflow-hidden",
        className,
      )}
      style={{ backgroundColor: section_background_color }}
    >
      {bgSrc ? (
        <>
          <Image
            src={bgSrc}
            alt={bgAlt || ""}
            fill
            className="object-cover object-center"
            sizes="100vw"
          />
          {overlayTrim ? (
            <div
              className="absolute inset-0 z-[1] pointer-events-none"
              style={{ backgroundColor: overlayTrim }}
              aria-hidden
            />
          ) : null}
        </>
      ) : null}

      <div
        className={cn(
          "relative z-10 w-full flex flex-col items-center",
          "min-[768px]:pt-9 min-[768px]:py-16 py-[52px] min-[768px]:px-10 min-[1024px]:px-14 px-[26px] min-[768px]:gap-20 gap-10",
        )}
      >
      <div className="flex flex-col gap-[30px] min-[768px]:gap-4 items-center mx-auto">
        <h2
          className="text-2xl min-[768px]:text-[32px] font-semibold text-center leading-[126%]"
          style={{ color: headline_primary_color }}
        >
          {renderHeadline(headline_primary, headline_accent_color)}
        </h2>
        {section_description ? (
          <p
            className="whitespace-pre-wrap w-[308px] min-[1024px]:w-[686px] text-sm min-[768px]:text-base text-center"
            style={{ color: headline_primary_color }}
          >
            {section_description}
          </p>
        ) : null}
      </div>

      <div className="flex">
        <div className="flex min-[768px]:flex-row min-[768px]:items-start flex-col flex-wrap justify-center min-[768px]:w-[866px] w-full mx-auto gap-x-0 min-[768px]:gap-y-20 gap-6 items-center">
          {cards.map((card, index) => (
            <div key={card.id} className="contents">
              <div
                className={cn(
                  "flex min-[1024px]:flex-row min-[1024px]:items-center min-[768px]:items-center flex-col items-center",
                  "min-[768px]:w-[280px] min-[1024px]:w-[366px] h-auto min-[768px]:h-fit min-[1024px]:h-fit gap-3 min-[768px]:gap-4",
                )}
              >
                {card.image ? (
                  <div className="relative w-14 h-14 min-[768px]:w-[52px] min-[768px]:h-[52px] shrink-0 rounded-lg">
                    <Image
                      src={card.image}
                      alt={card.imageAlt || card.title}
                      fill
                      className="object-contain"
                    />
                  </div>
                ) : null}
                <div className="flex flex-col items-center min-[768px]:items-start gap-1.5 w-[266px]">
                  <h3
                    className="font-semibold text-center min-[768px]:text-left w-[266px] min-[768px]:w-[296px] min-[768px]:text-xl"
                    style={{ color: headline_primary_color }}
                  >
                    {card.title}
                  </h3>
                  {card.description ? (
                    <p
                      className="text-[12px] text-center min-[768px]:text-left w-[266px] min-[768px]:w-[254px]"
                      style={{ color: headline_primary_color }}
                    >
                      {card.description}
                    </p>
                  ) : null}
                </div>
              </div>

              {/* Desktop divider after every odd-indexed card */}
              {index % 2 === 0 && index !== cards.length - 1 ? (
                <hr className="min-[1024px]:h-[112px] min-[768px]:h-[190px] bg-slate-50 min-[768px]:w-0.5 min-[768px]:mx-[60px] my-2 min-[768px]:my-0 min-[1024px]:block min-[768px]:block hidden border-0" />
              ) : null}
              {/* Mobile divider between every card */}
              {index !== cards.length - 1 ? (
                <hr className="h-[1px] w-full bg-slate-50 mx-2 my-2 min-[768px]:hidden block border-0" />
              ) : null}
            </div>
          ))}
        </div>
      </div>
      </div>
    </section>
  );
};

export default ProgramStructure5;
